/**
 * O RESUMO DO DIA SELECIONADO na Agenda — o "valor ao vivo" do hero de
 * `/agenda`.
 *
 * ─── POR QUE A AGENDA NÃO PODE PÔR DINHEIRO NO HERO ─────────────────────────
 *
 * O desenho oficial do app põe um valor ao vivo no cabeçalho, e o #182 já
 * avisou que **um número no hero é uma AFIRMAÇÃO**: se o rótulo dele puder ser
 * lido como outra coisa, o hero amplifica a mentira em `text-3xl`.
 *
 * Na Agenda o candidato óbvio seria a soma dos vencimentos do dia — e ela é
 * justamente a escolha errada, por dois motivos independentes:
 *
 * **(a) O dia mistura entrada e saída.** `finance_entries` do dia pode ter uma
 * conta a receber de R$ 1.200 e uma a pagar de R$ 900. Um número só teria que
 * escolher entre somar (dizendo que "entra" o que sai) ou compensar (um saldo
 * que não é saldo de nada — os dois nem sequer aconteceram ainda). Os dois
 * jeitos mentem, e nada na tela denunciaria.
 *
 * **(b) Dinheiro na Agenda tem GATE DE PAPEL** (#38/#187): `finance_entries` é
 * owner/admin, então para um `member` a soma seria R$ 0,00 — com a agenda dele
 * cheia de compromissos. É exatamente o "o `[]` da recusa vira R$ 0,00 em
 * `text-3xl`" que o #187 descreve.
 *
 * Por isso o valor ao vivo daqui é a CONTAGEM do dia selecionado — o mesmo
 * exercício que `/clients` e `/catalog` fizeram para listas sem dinheiro
 * (#195) — e ela é contada sobre os eventos JÁ VISÍVEIS, ou seja, depois do
 * gate de papel e do alternador de vencimentos. Assim o número nunca fala de
 * algo que a tela não mostra.
 *
 * O rótulo (a data) e a sublinha (a composição por tipo) são o que tornam a
 * contagem honesta: um "3" sozinho não diz se são três visitas ou três boletos.
 */
import type { CalendarEvent } from '@/lib/calendarEvents';
import { plural } from '@/lib/plural';

export interface AgendaDaySummary {
  /** Tudo que está visível naquele dia. */
  count: number;
  appointments: number;
  serviceOrders: number;
  financeEntries: number;
}

/**
 * Conta por TIPO os eventos de um dia.
 *
 * Recebe a lista já visível (gate de papel + alternador aplicados pela tela):
 * contar sobre a lista bruta faria o hero prometer um evento que o usuário não
 * consegue ver em lugar nenhum.
 */
export function summarizeAgendaDay(events: readonly CalendarEvent[]): AgendaDaySummary {
  const summary: AgendaDaySummary = {
    count: 0,
    appointments: 0,
    serviceOrders: 0,
    financeEntries: 0,
  };

  for (const event of events ?? []) {
    summary.count += 1;
    if (event?.kind === 'service_order') summary.serviceOrders += 1;
    else if (event?.kind === 'finance_entry') summary.financeEntries += 1;
    else summary.appointments += 1;
  }

  return summary;
}

/**
 * A composição do dia em texto: "1 compromisso · 2 serviços · 1 vencimento".
 *
 * Só entra o que EXISTE — um "0 vencimentos" ocuparia espaço para dizer que
 * nada acontece. Dia vazio devolve string vazia, e quem escreve a frase de
 * "nada marcado" é a tela, que é quem sabe se o usuário enxerga vencimentos.
 *
 * Toda contagem passa por `plural`: "1 serviços" foi defeito real neste app
 * (#87), e o caso 1 é justamente o que o autor do fixture não escreve.
 */
export function agendaDayBreakdown(summary: AgendaDaySummary): string {
  const parts: string[] = [];
  if (summary.appointments > 0) {
    parts.push(plural(summary.appointments, 'compromisso', 'compromissos'));
  }
  if (summary.serviceOrders > 0) {
    parts.push(plural(summary.serviceOrders, 'serviço', 'serviços'));
  }
  if (summary.financeEntries > 0) {
    parts.push(plural(summary.financeEntries, 'vencimento', 'vencimentos'));
  }
  return parts.join(' · ');
}

/** Meia-noite LOCAL do dia de uma data (nunca UTC — ver abaixo). */
function localMidnight(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * "Hoje" / "Amanhã" / "Ontem" para o rótulo do hero — `null` para qualquer
 * outro dia, e aí a tela escreve só a data por extenso.
 *
 * A diferença é medida entre MEIA-NOITES LOCAIS e arredondada: um dia de
 * horário de verão tem 23 ou 25 horas, então dividir a diferença bruta por 24h
 * erraria a fronteira justamente na virada (a família #36/#54/#114). Comparar
 * `toISOString` seria pior ainda: daria o dia em UTC e chamaria de "amanhã" o
 * que o usuário está vendo hoje às 22h.
 */
export function agendaDayRelativeLabel(date: Date, reference: Date = new Date()): string | null {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  if (!(reference instanceof Date) || Number.isNaN(reference.getTime())) return null;

  const days = Math.round(
    (localMidnight(date).getTime() - localMidnight(reference).getTime()) / 86_400_000,
  );
  if (days === 0) return 'Hoje';
  if (days === 1) return 'Amanhã';
  if (days === -1) return 'Ontem';
  return null;
}
